import classNames from 'classnames';
import { forwardRef, useCallback } from 'react';
import { useIntl } from 'react-intl';
import TapFeature from '@alltrails/amplitude/enums/TapFeature';
import logSaveClicked from '@alltrails/amplitude/events/logSaveClicked';
import logTrailCardClicked from '@alltrails/amplitude/events/logTrailCardClicked';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import getScreenReaderRatingText from '@alltrails/core/utils/getScreenReaderRatingText';
import { getFormattedHoursMinutesWithUnitsInSpans } from '@alltrails/data-formatting/dateTimeFormatters';
import Link from '@alltrails/denali/components/Link';
import LinkInfoRenderer from '@alltrails/denali/components/LinkInfoRenderer';
import StarFilled from '@alltrails/denali/icons/StarFilled';
import DifficultyIcon from '@alltrails/difficulty/components/DifficultyIcon';
import useFeatures from '@alltrails/features/hooks/useFeatures';
import { updateActiveTrails } from '@alltrails/maps/redux/reducer';
import InfoDividerDot from '@alltrails/shared/components/cards/InfoDividerDot';
import { getUrlFromSlug } from '@alltrails/shared/utils/cardLinks';
import { metersToFormattedDistance, ratingToFormattedRating } from '@alltrails/shared/utils/dataConversions';
import { AlgoliaTrail, getTrailDifficultyLabelFromNumber, trailFromAlgoliaTrail } from '@alltrails/trails';
import type { Visibility } from '@alltrails/types/Visibility';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import BookmarkButtonWrapper from './BookmarkButtonWrapper';
import styles from './TopResults.module.scss';
import TrailCardShowMoreText from './TrailCardShowMoreText';
import TrailCarousel from './TrailCarousel';

type Props = {
  trail: AlgoliaTrail;
  className?: string;
  ranking: number;
  isFavorite: boolean;
  onFavoriteClick: (itemType: 'trail' | 'map' | 'track', itemId: number, itemPrivacy?: Visibility | undefined) => void;
  isShowMoreClicked?: boolean;
  isTabletOrMobile?: boolean;
  focusable?: boolean;
};

const TopTrailsCard = forwardRef<HTMLDivElement, Props>(
  ({ trail, className, ranking, isFavorite, onFavoriteClick, isShowMoreClicked, isTabletOrMobile, focusable = false }, ref) => {
    const dispatch = useAppDispatch();
    const trailToRender = trailFromAlgoliaTrail(trail);
    const {
      name,
      avgRating,
      defaultActivityStats: { difficulty } = {},
      trailCounts: { reviewCount },
      trailGeoStats: { length, durationMinutes },
      trailDetail
    } = trailToRender;
    const displayMetric = useAppSelector(state => state.context.displayMetric);
    const languageRegionCode = useLanguageRegionCode();
    const intl = useIntl();
    const publicBaseUrl = usePublicBaseUrl();
    const href = getUrlFromSlug(`/${trail.slug}`, publicBaseUrl, languageRegionCode);
    const localizedRating = ratingToFormattedRating(avgRating, languageRegionCode);
    const difficultyText = getTrailDifficultyLabelFromNumber(intl, difficulty);
    const minutes = parseInt(durationMinutes ?? '0', 10);
    const isRoundEstTimeToCompletionEnabled = useFeatures()?.includes('round_est_time_completion');

    const handleTrailClick = useCallback(
      (tapFeature: TapFeature) => {
        logTrailCardClicked({
          trail_id: String(trail.ID),
          card_location: 'location page',
          detailed_card_location: 'location page list',
          tap_feature: tapFeature
        });
      },
      [trail.ID]
    );

    const handleFavoriteClick = () => {
      logSaveClicked({
        detailed_card_location: 'location page list',
        save_location: 'location page',
        map_id: trail.ID,
        map_type: 'trail'
      });
      onFavoriteClick('trail', trail.ID);
    };

    const handleSetHoveredResult = useCallback(() => {
      if (trail._geoloc !== undefined) {
        dispatch(updateActiveTrails([trail]));
      }
    }, [dispatch, trail]);

    const handleClearHoveredResult = useCallback(() => dispatch(updateActiveTrails(undefined)), [dispatch]);
    const hasDescription = Boolean(trailDetail?.description);

    return (
      <div
        ref={ref}
        role="group"
        aria-label="trail"
        className={classNames(styles.trailCardSnippetContainer, className)}
        title={name}
        {...(focusable && { tabIndex: -1 })}
        {...(!isTabletOrMobile && {
          onMouseEnter: handleSetHoveredResult,
          onMouseLeave: handleClearHoveredResult
        })}
      >
        <div className={styles.resultCardContent}>
          <Link href={href} target="_blank" onClick={() => handleTrailClick('trail image')}>
            <TrailCarousel trail={trailToRender} defaultUrl={!isShowMoreClicked ? trail.profile_photo_url : undefined} />
          </Link>
          <div className={styles.titleAndBookmark}>
            <div className={styles.titleAndStats}>
              <div className={styles.rankNameAndBookmark}>
                <h3 className={styles.trailCardHeading}>
                  <LinkInfoRenderer
                    linkInfo={{ href, target: '_blank' }}
                    className={styles.resultName}
                    onClick={() => handleTrailClick('trail name')}
                  >
                    <span className={styles.desktopRanking}>#{ranking} - </span>
                    {name}
                  </LinkInfoRenderer>
                </h3>
                {!isTabletOrMobile && <BookmarkButtonWrapper isFavorite={isFavorite} onClick={handleFavoriteClick} />}
              </div>
              <Link className={styles.resultStatsLink} href={href} target="_blank" title={name}>
                <div className={styles.resultStats}>
                  {!!avgRating && (
                    <span className={styles.star} role="img" aria-label={getScreenReaderRatingText(intl, avgRating)}>
                      <StarFilled color="--color-text-secondary" size="xs" />
                    </span>
                  )}
                  <div className={styles.textContent}>
                    {!!avgRating && (
                      <span className={styles.rating} data-testid="rating">
                        {localizedRating}
                      </span>
                    )}
                    {!!reviewCount && (
                      <span
                        className={styles.reviews}
                        data-testid="review-count"
                        aria-label={intl.formatMessage({ defaultMessage: '{count} reviews' }, { count: intl.formatNumber(reviewCount) })}
                      >
                        ({reviewCount})
                      </span>
                    )}
                    {!!difficulty && (
                      <div data-testid="difficulty" className={styles.resultStat}>
                        {(!!avgRating || !!reviewCount) && <InfoDividerDot className={styles.infoDot} />}
                        <span className={styles.difficultyIcon}>
                          <DifficultyIcon difficulty={difficulty} size="xs" />
                        </span>
                        <span>{difficultyText}</span>
                      </div>
                    )}
                    {!!length && (
                      <div data-testid="distance" className={styles.resultStat}>
                        {(!!difficulty || !!reviewCount) && <InfoDividerDot className={styles.infoDot} />}
                        <span>{metersToFormattedDistance(intl, languageRegionCode, length, displayMetric ? 'km' : 'mi', 1)}</span>
                      </div>
                    )}
                    {!!minutes && (
                      <div className={styles.estimatedTime} data-testid="duration">
                        {isRoundEstTimeToCompletionEnabled && trail?.estimated_time_to_complete ? (
                          <>
                            <InfoDividerDot className={styles.infoDot} />
                            {`${intl.formatMessage({ defaultMessage: ' Est.' })} ${trail.estimated_time_to_complete.value} ${trail.estimated_time_to_complete.unit}`}
                          </>
                        ) : (
                          <>
                            <InfoDividerDot className={styles.infoDot} />
                            {intl.formatMessage({ defaultMessage: ' Est.' })}{' '}
                            {getFormattedHoursMinutesWithUnitsInSpans(intl, minutes * 60, true, true)}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
              {!isTabletOrMobile && hasDescription && (
                <TrailCardShowMoreText
                  trailId={trail.ID}
                  trailSlug={trail.slug}
                  trailName={name}
                  className={styles.trailCardSnippetDescription}
                  testIdPrefix={`${trail.id}-description-main`}
                  description={trailDetail?.description}
                />
              )}
            </div>
            {isTabletOrMobile && <BookmarkButtonWrapper isFavorite={isFavorite} onClick={handleFavoriteClick} />}
          </div>
        </div>
        {isTabletOrMobile && hasDescription && (
          <div className={styles.trailCardSnippetDescriptionContainer}>
            <TrailCardShowMoreText
              trailId={trail.ID}
              trailSlug={trail.slug}
              trailName={name}
              className={styles.trailCardSnippetDescription}
              testIdPrefix={`${trail.id}-description-snippet`}
              description={trailDetail?.description}
            />
          </div>
        )}
      </div>
    );
  }
);

TopTrailsCard.displayName = 'TopTrailsCard';

export default TopTrailsCard;
